/**
 * AI Call Cooldown — prevents rapid-fire AI requests
 *
 * Enforces a minimum time between AI calls per conversation/context.
 * This prevents accidental spam (double-tap, fast resend) and protects
 * against runaway loops that could rack up API costs.
 *
 * Usage:
 *   import { canCallAI, recordAICall } from './aiCooldown.js';
 *
 *   if (!canCallAI(conversationId)) {
 *     console.log('AI cooldown active — please wait');
 *     return;
 *   }
 *   recordAICall(conversationId);
 *   // ... make the AI call
 */

const COOLDOWN_MS = 3000; // 3 seconds between AI calls per context
const lastCallMap = new Map();

/**
 * Check if an AI call is allowed for the given context key.
 * @param {string} contextKey — conversation ID, channel ID, or phone session ID
 * @returns {boolean} true if enough time has passed since the last call
 */
export function canCallAI(contextKey) {
  const lastCall = lastCallMap.get(contextKey);
  if (!lastCall) return true;
  return Date.now() - lastCall >= COOLDOWN_MS;
}

/**
 * Record that an AI call was just made for the given context.
 * @param {string} contextKey
 */
export function recordAICall(contextKey) {
  lastCallMap.set(contextKey, Date.now());
  // Cleanup old entries to prevent memory leaks (keep last 50)
  if (lastCallMap.size > 50) {
    const entries = [...lastCallMap.entries()].sort((a, b) => a[1] - b[1]);
    for (let i = 0; i < entries.length - 50; i++) {
      lastCallMap.delete(entries[i][0]);
    }
  }
}

